'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/app/hooks/useToast';
import { PropsWithClassname } from '@/app/types';

import { ProjectPhoto, SubprojectStateStatusEnum } from '@/app/api';

import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import Loader from '@/app/components/Loader/Loader';
import ProjectCard from '@/app/components/ProjectCard/ProjectCard';
import { countryConfig } from '@/app/utils/countryConfig';
import { getPriceInCents } from '@/app/utils/numbers';
import { useGetSubprojectsInfinite } from '@/src/api/client';

interface SubprojectListProps extends PropsWithClassname {
  projectId: string;
  projectUrl: string;
  title: string;
  projectMainImage?: ProjectPhoto;
  statuses: SubprojectStateStatusEnum[];
  onTotalCountChange?: (totalItemsCount: number | null) => void;
}

const SubprojectList = ({
  projectId,
  projectUrl,
  projectMainImage,
  title,
  onTotalCountChange,
  statuses,
  className,
}: SubprojectListProps) => {
  const { t } = useTranslation();
  const { showGenericErrorToast } = useToast();

  const { data, isLoading, error, fetchNextPage, hasNextPage } = useGetSubprojectsInfinite(
    {
      project: projectId,

      statuses,
      minimalCurrentAmountInCents: getPriceInCents(countryConfig.minimalAmountForGoodHabitToView),
    },
    {
      query: {
        getNextPageParam: (lastPage, allPages) => {
          const loadedItemCount = allPages.flatMap((p) => p.subprojects).length;
          const totalItemCount = lastPage.totalItemCount ?? 0;

          if (loadedItemCount < totalItemCount) {
            return allPages.length + 1; // next page number
          }

          return undefined; // no more pages
        },
      },
    },
  );

  const totalCount = data?.pages[0].totalItemCount ?? null;
  const items = data?.pages.flatMap((p) => [...p.subprojects]) ?? [];

  useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [totalCount]);

  useEffect(() => {
    if (error) {
      showGenericErrorToast();
    }
  }, [error, showGenericErrorToast, t]);

  return (
    <div className={className}>
      {!!items.length && (
        <>
          <h3 className="mb-[20px] text-[24px] leading-[32px] font-medium text-balance">{title}</h3>
          <div className="overflow-x-auto flex lg:flex-wrap mb-[40px] mx-[-24px] lg:mx-0">
            {items.map((subproject) => (
              <ProjectCard
                key={subproject.id}
                title={subproject.content.name}
                image={subproject.content.mainPhoto ?? projectMainImage ?? undefined}
                href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}${projectUrl}/${subproject.slug}`}
                currentAmountInCents={subproject.statistics.currentAmountInCents}
                targetAmountInCents={subproject.statistics.targetAmountInCents ?? undefined}
                donationsCount={subproject.statistics.donationsCount}
                className="first:ml-[16px] lg:first:ml-0 mr-[16px] mb-[16px]"
              />
            ))}
          </div>
        </>
      )}
      {isLoading && (
        <>
          <h3 className="mb-[20px] text-[24px] leading-[32px] font-medium text-balance">{title}</h3>
          <Loader />
        </>
      )}
      {hasNextPage && (
        <Button
          testId="load-more-donation-button"
          className={'block self-center px-[24px] py-[20px] mx-[auto] mb-[40px]'}
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.BUTTON,
            onClick: () => fetchNextPage(),
          }}
        >
          {t('components.ProjectDonorsTab.moreDonations')}
        </Button>
      )}
    </div>
  );
};

export default SubprojectList;
